import React, { useEffect, useState } from "react";
import { IntlShape } from "react-intl";
import { FieldAttributes } from "formik";
import { match as Match } from "react-router-dom";
import { toast } from "react-toastify";
import Block from "../../../../../../components/Tailwind/Block";
import Header from "../../../../../../components/Tailwind/Block/Header";
import DefaultForm, {
  AdditionalFieldAttributes,
} from "../../../../../../components/Form";
import overrideClasses from "../../../../../../utils/overrideClasses";
import { GroundGraphqlContextStore } from "../../../../../../lib/ground-aws-graphql-core";
import { isString } from "../../../../../../utils/types";
import history from "../../../../../../history";
import { EnumPaths } from "../../../../../../utils/navigation";
import { EnumPermissionEntity } from "../../../../../../lib/ground-aws-graphql-core/api/graphql/types";

interface Props {
  match: Match<{ cid: string; typeId: string; checkpointId?: string }>;
  intl: IntlShape;
  edition: boolean;
}

const CheckpointForm = (props: Props): JSX.Element => {
  const { intl, match, edition } = props;

  const [enabled, setEnabled] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(false);
  const [togglePanel, setTogglePanel] = useState(false);

  const getCheckpoint = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.getCheckpoint
  );

  const createCheckpoint = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.createCheckpoint
  );

  const updateCheckpoint = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.updateCheckpoint
  );

  const setCheckpoint = GroundGraphqlContextStore.useStoreActions(
    actions => actions.inspection.setCheckpoint
  );

  const checkpoint = GroundGraphqlContextStore.useStoreState(
    state => state.inspection.checkpoint
  );

  const formFields: FieldAttributes<AdditionalFieldAttributes>[] = [
    {
      name: "title",
      label: "page.inspection.checkpoint.label",
      placeholder: "page.inspection.checkpoint.label.placeholder",
      initialValue: checkpoint?.title,
      required: true,
      translatable: true,
    },
  ];

  const fetchData = () => {
    setLoading(true);
    if (match.params.checkpointId) {
      getCheckpoint({
        checkpointTypeId: match.params.typeId,
        checkpointId: match.params.checkpointId,
      })
        .catch(() => {
          toast(
            intl.formatMessage({
              id: "page.inspection.checkpoint.get.error",
            }),
            {
              type: "error",
            }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();

    // Clean the event from the state when we unmount this component
    return () => {
      setCheckpoint(null);
    };
  }, []);

  useEffect(() => {
    if (checkpoint) setEnabled(checkpoint.enabled);
  }, [checkpoint]);

  const handleShowSavePanel = () => {
    setTogglePanel(true);
  };

  const handleSubmit = (values: any) => {
    setLoading(true);
    const { title, ...others } = values;
    if (edition && match.params.checkpointId) {
      updateCheckpoint({
        checkpointTypeId: match.params.typeId,
        checkpointId: match.params.checkpointId,
        input: {
          ...others,
          title: isString(title) ? JSON.parse(title) : title,
          enabled,
        },
      })
        .then(() => {
          history.push(
            `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${match.params.typeId}/${EnumPaths.EDIT_MODE}`
          );
          toast(
            intl.formatMessage({
              id: "page.inspection.checkpoint.update.success",
            }),
            { type: "success" }
          );
        })
        .catch(() => {
          history.push(
            `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${match.params.typeId}/${EnumPaths.EDIT_MODE}`
          );
          toast(
            intl.formatMessage({
              id: "page.inspection.checkpoint.update.error",
            }),
            {
              type: "error",
            }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    } else {
      createCheckpoint({
        checkpointTypeId: match.params.typeId,
        input: {
          ...others,
          title: isString(title) ? JSON.parse(title) : title,
          enabled,
        },
      })
        .then(() => {
          history.push(
            `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${match.params.typeId}/${EnumPaths.EDIT_MODE}`
          );
          toast(
            intl.formatMessage({
              id: "page.inspection.checkpoint.create.success",
            }),
            { type: "success" }
          );
        })
        .catch(() => {
          history.push(
            `/${EnumPaths.ROOT}/${EnumPaths.CENTERS}/${match.params.cid}/${EnumPaths.CHECKPOINT_TYPES}/${match.params.typeId}/${EnumPaths.EDIT_MODE}`
          );
          toast(
            intl.formatMessage({
              id: "page.inspection.checkpoint.create.error",
            }),
            {
              type: "error",
            }
          );
        })
        .finally(() => {
          setLoading(false);
        });
    }
  };

  return loading ? (
    <div className="loading" />
  ) : (
    <>
      {/**
       * When we were loading the form was initialized again and we would lose all data
       * Show loading here in order to not lose the fields data after a loading
       */}
      <div className={overrideClasses({ loading })} />

      {/* Hide when loading */}
      <div className={overrideClasses("px-8 pb-32", { hidden: loading })}>
        <Block>
          <Header
            item={checkpoint}
            checked={enabled}
            title={
              edition
                ? "page.list.inspection.checkpoints.update.checkpoint"
                : "page.list.inspection.checkpoints.create.checkpoint"
            }
            entity={EnumPermissionEntity.CHECKPOINT}
            onChange={e => {
              setEnabled(e);

              // show save panel
              handleShowSavePanel();
            }}
            className="border-b border-gray-200"
          />

          <DefaultForm
            fields={formFields}
            onSubmit={handleSubmit}
            showPanel={togglePanel}
          />
        </Block>
      </div>
    </>
  );
};

export default CheckpointForm;
